<script setup lang="ts">
import CommonTable from '@/components/common/table/CommonTable.vue'
import { useMemberManagement } from '@/composables/memberManagement/useMemberManagement'
import { FIELDS_TYPED } from '@/constants/userManagement/tableName'
import { setLoadingView } from '@/features/miiaFw/utils/miiaFwUtils'
import type { TableFields } from '@/types/component/common/table/commonTable'
import type { UserManagement } from '@/types/userManagement/userManagement'
import { computed, onMounted, ref } from 'vue'
import { BFormInput } from 'bootstrap-vue-next'
import { BIconPencil } from 'bootstrap-icons-vue'
import { toDomestic } from '@/utils/converter/phoneNumberConverter'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { PHONE_NUMBER_LENGTH } from '@/constants/userManagement/validation'

/**
 * チェックされているユーザーIDの配列を管理するモデル
 */
const checkedItems = defineModel<string[]>('checkedItems')

/**
 * ユーザ管理のComposableから取得したデータと処理群
 */
const {
  itemsTyped,
  fetchUserInfoForOrgAdmin,
  editingCell,
  reactiveFieldValue,
  startEdit,
  stopEdit
} = useMemberManagement()

/**
 * テーブルの表示フィールドを computed で動的に切り替える
 */
const fieldsTyped = computed<TableFields<Partial<UserManagement>>>(() => {
  return FIELDS_TYPED.filter(field => field.key !== 'organizationName')
})

/**
 * 現在ホバー中のセル情報を保持するリアクティブ変数
 * ペンアイコン表示制御に使用
 */
const hoveredCell = ref<{ rowIndex: number | null; field: string | null }>({
  rowIndex: null,
  field: null
})

/**
 * セルにマウスが乗った時に呼ばれる
 * ホバー情報を更新しペンアイコンを表示するために利用
 * @param rowIndex ホバー対象行番号
 * @param field ホバー対象フィールド名
 */
function onMouseEnter(rowIndex: number, field: string) {
  hoveredCell.value = { rowIndex, field }
}

/**
 * セルからマウスが離れた時に呼ばれる
 * ホバー情報をリセットしペンアイコンを非表示にするために利用
 */
function onMouseLeave() {
  hoveredCell.value = { rowIndex: null, field: null }
}

/**
 * 編集内容を確定する。
 * @param rowIndex 行番号
 * @param field フィールド名
 */
const confirmEdit = async (rowIndex: number | null, field: keyof UserManagement | null) => {
  try {
    setLoadingView(true)
    await stopEdit(rowIndex, field)
  } catch (e) {
    throw GlobalError.chainError(e, ERROR_CODES._811303_UPDATE_USER_ERROR)
  } finally {
    setLoadingView(false)
  }
}

/**
 * コンポーネントのmounted時に自治体か団体管理者かに応じてユーザー情報を取得しセットする
 * 取得後はローディング表示を解除する
 */
onMounted(async () => {
  try {
    setLoadingView(true)
    await fetchUserInfoForOrgAdmin()
  } catch (e) {
    throw GlobalError.chainError(e, ERROR_CODES._811301_FETCH_USER_ERROR)
  } finally {
    setLoadingView(false)
  }
})
</script>

<template>
  <div>
    <CommonTable :itemsTyped="itemsTyped" :fieldsTyped="fieldsTyped" class="fs-5 table">
      <!-- ユーザ名 -->
      <template #cell(userName)="{ row }">
        <div
          class="editable-cell"
          @mouseenter="onMouseEnter(row.index, 'userName')"
          @mouseleave="onMouseLeave"
        >
          <template v-if="editingCell.rowIndex === row.index && editingCell.field === 'userName'">
            <b-form-input
              v-model="reactiveFieldValue"
              size="sm"
              autofocus
              @blur="() => confirmEdit(row.index, 'userName')"
              @keyup.enter="() => confirmEdit(row.index, 'userName')"
            />
          </template>
          <template v-else>
            <span>{{ row.item.userName }}</span>
            <b-icon-pencil
              v-if="hoveredCell.rowIndex === row.index && hoveredCell.field === 'userName'"
              class="edit-icon"
              @click="startEdit(row.index, 'userName')"
            />
          </template>
        </div>
      </template>
      <!-- 電話番号 -->
      <template #cell(phoneNumber)="{ row }">
        <div
          class="editable-cell"
          @mouseenter="onMouseEnter(row.index, 'phoneNumber')"
          @mouseleave="onMouseLeave"
        >
          <template
            v-if="editingCell.rowIndex === row.index && editingCell.field === 'phoneNumber'"
          >
            <b-form-input
              v-model="reactiveFieldValue"
              size="sm"
              autofocus
              @blur="() => confirmEdit(row.index, 'phoneNumber')"
              @keyup.enter="() => confirmEdit(row.index, 'phoneNumber')"
              :maxLength="PHONE_NUMBER_LENGTH"
            />
          </template>
          <template v-else>
            <span>{{ toDomestic(row.item.phoneNumber as string) }}</span>
            <b-icon-pencil
              v-if="hoveredCell.rowIndex === row.index && hoveredCell.field === 'phoneNumber'"
              class="edit-icon"
              @click="startEdit(row.index, 'phoneNumber')"
            />
          </template>
        </div>
      </template>
      <template #cell(deleteCheckbox)="{ row }">
        <b-form-checkbox-group v-model="checkedItems">
          <b-form-checkbox :value="row.item.userId as string" />
        </b-form-checkbox-group>
      </template>
    </CommonTable>
  </div>
</template>

<style lang="scss" scoped>
.editable-cell {
  position: relative;
  padding-right: 25px;
  min-height: 1em;
}

.editable-cell .edit-icon {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #666;
  transition: color 0.3s ease;
  font-size: 1.25em;
}

.editable-cell .edit-icon:hover {
  color: #007bff;
}
</style>
